import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/lib/api/profile';
import { onProfileMutationSuccess } from '@/lib/profile-query';
import type { ProfileCertificationDto, UpdateProfileDto } from '@paladin-monorepo/common';
import { SuccessIndicator } from '@/components/ui/success-indicator';
import { Trash2 } from 'lucide-react';

function emptyRow(): ProfileCertificationDto {
  return { name: '', issuer: null, year: null };
}

export function SupplierCertificationsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const [rows, setRows] = useState<ProfileCertificationDto[]>([emptyRow()]);
  const initialized = useRef(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  });

  useEffect(() => {
    if (!profile || initialized.current) return;
    initialized.current = true;
    const list = profile.certifications?.length ? profile.certifications : [emptyRow()];
    setRows(list.map((c) => ({ ...c })));
  }, [profile]);

  const mutation = useMutation({
    mutationFn: (dto: UpdateProfileDto) => profileApi.updateProfile(dto),
    onSuccess: (data) => {
      const list = data.certifications?.length ? data.certifications : [emptyRow()];
      setRows(list.map((c) => ({ ...c })));
      onProfileMutationSuccess(queryClient, data);
      setShowSuccess(true);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    },
  });

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const save = () => {
    const cleaned = rows
      .map((r) => {
        const rawYear =
          r.year === null || r.year === undefined || String(r.year) === ''
            ? null
            : typeof r.year === 'number'
              ? r.year
              : parseInt(String(r.year), 10);
        const year = rawYear !== null && !Number.isNaN(rawYear) ? rawYear : null;
        return {
          name: r.name.trim(),
          issuer: r.issuer?.trim() ? r.issuer.trim() : null,
          year,
        };
      })
      .filter((r) => r.name.length > 0);
    mutation.mutate({ certifications: cleaned });
  };

  const updateRow = (index: number, patch: Partial<ProfileCertificationDto>) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const removeRow = (index: number) => {
    setRows((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((_, i) => i !== index)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('profile.certificationsStructured.title')}
          <SuccessIndicator visible={showSuccess} />
        </CardTitle>
        <CardDescription>{t('profile.certificationsStructured.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div>{t('generic.loading')}</div>
        ) : (
          <>
            <div className="space-y-4">
              {rows.map((row, index) => (
                <div
                  key={index}
                  className="grid gap-3 sm:grid-cols-[1fr_1fr_minmax(0,7rem)_auto] items-end border-b border-border pb-4 last:border-0"
                >
                  <div className="space-y-1">
                    <Label>{t('profile.certificationsStructured.name')}</Label>
                    <Input
                      value={row.name}
                      onChange={(e) => updateRow(index, { name: e.target.value })}
                      placeholder={t('profile.certificationsStructured.namePlaceholder')}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>{t('profile.certificationsStructured.issuer')}</Label>
                    <Input
                      value={row.issuer ?? ''}
                      onChange={(e) =>
                        updateRow(index, { issuer: e.target.value === '' ? null : e.target.value })
                      }
                      placeholder={t('profile.certificationsStructured.issuerPlaceholder')}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>{t('profile.certificationsStructured.year')}</Label>
                    <Input
                      type="number"
                      min={1900}
                      max={2100}
                      value={row.year ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateRow(index, {
                          year: v === '' ? null : parseInt(v, 10),
                        });
                      }}
                      placeholder={t('profile.certificationsStructured.yearPlaceholder')}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(index)}
                    aria-label={t('profile.certificationsStructured.remove')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={addRow}>
                {t('profile.certificationsStructured.add')}
              </Button>
              <Button type="button" onClick={save} disabled={mutation.isPending}>
                {t('profile.certificationsStructured.save')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
